import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  TextField,
  Button,
  Alert,
  Chip,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Link,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';

import { getPlans, redeemDiscount, removeDiscount, resetDiscountError } from '../modules/plans';
import { formatLargeNumber } from '../helper.js';
import { glintOnHover } from '../theme/glint';

const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : str);

/**
 * Translated message for an error code of POST discount/redeem (422/429) or POST plan/{plan} (plan_not_allowed).
 */
export const discountErrorMessage = (t, discountError, code) => {
  if (!discountError) return null;
  const { error } = discountError;
  if (error === 'rate_limited') {
    return discountError.retry_after
      ? t('plans.discountCode.errors.rate_limited', { minutes: Math.max(1, Math.ceil(discountError.retry_after / 60)) })
      : t('plans.discountCode.errors.rate_limited_unknown');
  }
  if (error === 'current_plan_not_allowed') {
    return t('plans.discountCode.errors.current_plan_not_allowed', {
      plans: (discountError.allowed_plans || []).map(capitalize).join(', '),
    });
  }
  if (error === 'plan_not_allowed') {
    return t('plans.discountCode.errors.plan_not_allowed', {
      code: code || '',
      plans: (discountError.allowed_plans || []).map(capitalize).join(', '),
    });
  }
  const known = ['invalid', 'expired', 'used', 'in_use', 'not_eligible', 'code_in_use', 'empty', 'network'];
  return t(`plans.discountCode.errors.${known.includes(error) ? error : 'unknown'}`);
};

/**
 * Benefits of the pending code as {kind, text}: discount (+ duration), trial, bonus words, allowed plans.
 */
const discountBenefitItems = (t, rules) => {
  if (!rules) return [];
  const benefits = [];
  if (rules.discount && rules.discount.type) {
    const value = rules.discount.type === 'percentage'
      ? t('plans.discountCode.benefitDiscountPercent', { value: rules.discount.value })
      : t('plans.discountCode.benefitDiscountAmount', { value: parseFloat(rules.discount.value).toFixed(2) });
    const duration = rules.discount.durationMonths
      ? `${t('plans.discountCode.benefitDuration', { count: rules.discount.durationMonths })}, ${t('plans.discountCode.thenRegularPrice')}`
      : t('plans.discountCode.benefitPermanent');
    benefits.push({ kind: 'discount', text: `${value} ${duration}` });
  }
  if (rules.trialDays) {
    // whole 30-day intervals in months, like the staff gives them
    const trial = rules.trialDays % 30 === 0
      ? t('plans.discountCode.benefitTrialMonths', { count: rules.trialDays / 30 })
      : t('plans.discountCode.benefitTrial', { count: rules.trialDays });
    const then = rules.discount && rules.discount.type ? t('plans.discountCode.thenDiscountedPrice') : t('plans.discountCode.thenRegularPrice');
    benefits.push({ kind: 'trial', text: `${trial}, ${then}` });
  }
  if (rules.wordsOnActivation) {
    benefits.push({ kind: 'words', text: t('plans.discountCode.benefitWords', { words: formatLargeNumber(rules.wordsOnActivation) }) });
  }
  if (rules.allowedPlans && rules.allowedPlans.length > 0) {
    benefits.push({ kind: 'plans', text: t('plans.discountCode.allowedPlans', { plans: rules.allowedPlans.map(capitalize).join(', ') }) });
  }
  return benefits;
};

const formatExpiry = (expiresAt) => {
  const date = new Date(expiresAt);
  if (isNaN(date.valueOf())) return null;
  // a reservation lasts 24-72 h: no year, short month, so the line fits the dialog
  return date.toLocaleString(navigator.language, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const BENEFIT_ICONS = {
  discount: PercentRoundedIcon,
  trial: EventAvailableOutlinedIcon,
  words: AutoAwesomeOutlinedIcon,
  plans: WorkspacePremiumOutlinedIcon,
};

// errors of a plan change (POST plan/{plan}) concern the code, so the overlay opens to show them
const PLAN_CHANGE_ERRORS = ['plan_not_allowed', 'code_in_use'];

const styles = {
  paper: (theme) => ({
    borderRadius: 4,
    overflow: 'hidden',
    backgroundImage: 'none',
    boxShadow: `0 24px 64px ${alpha(theme.palette.primary.main, 0.28)}`,
  }),
  header: (theme) => ({
    position: 'relative',
    overflow: 'hidden',
    px: 3,
    pt: 4,
    pb: 3,
    textAlign: 'center',
    color: theme.palette.primary.contrastText,
    // the gradient of the page header (index.css), light and dark mode
    backgroundColor: theme.palette.primary.main,
    backgroundImage: 'var(--ly-banner-gradient)',
  }),
  close: {
    position: 'absolute',
    top: 8,
    right: 8,
    color: 'inherit',
    zIndex: 1,
  },
  badge: {
    width: 64,
    height: 64,
    mx: 'auto',
    mb: 1.5,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: alpha('#ffffff', 0.18),
    border: `1px solid ${alpha('#ffffff', 0.35)}`,
    '& svg': { fontSize: 32 },
  },
  codeInput: {
    '& input': {
      textAlign: 'center',
      textTransform: 'uppercase',
      letterSpacing: '0.18em',
      fontWeight: 600,
      fontSize: '1.15rem',
      py: 1.75,
    },
    // the placeholder is an example, not a code: normal spelling
    '& input::placeholder': { textTransform: 'none', letterSpacing: 'normal', fontWeight: 400 },
    '& .MuiOutlinedInput-root': { borderRadius: 2 },
  },
  primaryButton: [{ py: 1.25, borderRadius: 2, fontWeight: 600 }, glintOnHover],
  codeChip: (theme) => ({
    fontWeight: 700,
    letterSpacing: '0.12em',
    fontSize: '1rem',
    height: 36,
    px: 1,
    borderStyle: 'dashed',
    borderWidth: 2,
    borderColor: theme.palette.success.main,
    color: theme.palette.success.main,
    backgroundColor: alpha(theme.palette.success.main, 0.06),
  }),
  benefit: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 1.5,
    py: 1,
    borderRadius: 2,
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.05),
  }),
  benefitIcon: (theme) => ({
    width: 32,
    height: 32,
    flexShrink: 0,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.primary.contrastText,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    '& svg': { fontSize: 18 },
  }),
  trigger: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    py: 2,
  },
};

const DiscountCode = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const discount = useSelector((state) => state.plans.discount);
  const discountPending = useSelector((state) => state.plans.discount_pending);
  const discountError = useSelector((state) => state.plans.discount_error);
  const featureList = useSelector((state) => state.plans.featureList);

  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState(null);
  const [removed, setRemoved] = useState(false);
  // instant words code: {words, code} after the words were credited
  const [wordsCredited, setWordsCredited] = useState(null);
  const [now, setNow] = useState(Date.now());

  // The plans may come from an earlier visit (dashboard), reload them so the pending code is up to date.
  // On the first visit PlanSelect loads them.
  useEffect(() => {
    if (featureList && !Array.isArray(featureList)) {
      dispatch(getPlans());
    }
    return () => dispatch(resetDiscountError());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (discountError && PLAN_CHANGE_ERRORS.includes(discountError.error)) {
      setOpen(true);
    }
  }, [discountError]);

  // Countdown, and reload the plans once the code has expired (the backend drops it after 24h).
  useEffect(() => {
    if (!discount || !discount.expires_at) return undefined;
    const expiresAt = new Date(discount.expires_at).valueOf();
    if (isNaN(expiresAt)) return undefined;
    const interval = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= expiresAt) {
        clearInterval(interval);
        dispatch(getPlans());
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [discount, dispatch]);

  // A link like /profile/plans?code=6KRRFJLEAZS2 redeems the code right away and shows it in the dialog.
  // The parameter is removed afterwards, so a reload does not redeem it again.
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlCode = (params.get('code') || '').trim();
    if (!urlCode) return;
    params.delete('code');
    const search = params.toString();
    history.replace({ pathname: location.pathname, search: search ? '?' + search : '', hash: location.hash });
    setRemoved(false);
    setLocalError(null);
    setCode(urlCode);
    setOpen(true);
    dispatch(redeemDiscount(urlCode, (response) => {
      if (response && response.success === true) {
        setCode('');
        if (response.words_credited) setWordsCredited({ words: response.words_credited, code: response.words_code });
      }
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleOpen = () => {
    setRemoved(false);
    setWordsCredited(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setWordsCredited(null);
    setLocalError(null);
    dispatch(resetDiscountError());
  };

  // the discount shows on the plan cards above, so close and bring them into view
  const handleChoosePlan = () => {
    handleClose();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRedeem = (e) => {
    if (e) e.preventDefault();
    const trimmed = code.trim();
    setRemoved(false);
    if (!trimmed) {
      setLocalError({ error: 'empty' });
      return;
    }
    setLocalError(null);
    dispatch(redeemDiscount(trimmed, (response) => {
      if (response && response.success === true) {
        setCode('');
        if (response.words_credited) setWordsCredited({ words: response.words_credited, code: response.words_code });
      }
    }));
  };

  const handleRemove = () => {
    setLocalError(null);
    dispatch(removeDiscount((response) => {
      if (response && response.success === true) {
        setRemoved(true);
      }
    }));
  };

  const errorMessage = discountErrorMessage(t, localError || discountError, discount && discount.code);
  const hasCode = !!(discount && discount.code);
  const benefits = hasCode ? discountBenefitItems(t, discount.rules) : [];

  let expiresIn = null;
  if (hasCode && discount.expires_at) {
    const diff = new Date(discount.expires_at).valueOf() - now;
    if (diff > 0) {
      expiresIn = {
        hours: Math.floor(diff / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      };
    }
  }
  const expiryDate = hasCode && discount.expires_at ? formatExpiry(discount.expires_at) : null;

  return (
    <>
      <Box id="discountCode" sx={styles.trigger}>
        {hasCode ? (
          <>
            <LocalOfferOutlinedIcon color="success" fontSize="small" />
            <Typography variant="body2" component="span" data-testid="discount-code-summary">
              {t('plans.discountCode.activeSummary', { code: discount.code })}
            </Typography>
            <Link component="button" type="button" variant="body2" onClick={handleOpen} underline="hover" sx={{ fontWeight: 600 }} data-testid="discount-code-open">
              {t('plans.discountCode.showDetails')}
            </Link>
          </>
        ) : (
          <>
            <LocalOfferOutlinedIcon color="action" fontSize="small" />
            <Typography variant="body2" component="span" color="text.secondary">
              {t('plans.discountCode.triggerQuestion')}
            </Typography>
            <Link component="button" type="button" variant="body2" onClick={handleOpen} underline="hover" sx={{ fontWeight: 600 }} data-testid="discount-code-open">
              {t('plans.discountCode.triggerLink')}
            </Link>
          </>
        )}
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
        aria-labelledby="discount-code-title"
        slotProps={{ paper: { sx: styles.paper, 'data-testid': 'discount-code-dialog' } }}
      >
        <Box sx={styles.header}>
          <IconButton aria-label={t('plans.discountCode.close')} onClick={handleClose} sx={styles.close} size="small" data-testid="discount-code-close">
            <CloseIcon fontSize="small" />
          </IconButton>
          <Box sx={styles.badge}>
            {hasCode || wordsCredited ? <CheckCircleRoundedIcon /> : <LocalOfferOutlinedIcon />}
          </Box>
          <Typography id="discount-code-title" variant="h5" component="h2" sx={{ fontWeight: 700, color: 'inherit' }}>
            {wordsCredited ? t('plans.discountCode.wordsTitle') : hasCode ? t('plans.discountCode.successTitle') : t('plans.discountCode.dialogTitle')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9, color: 'inherit' }}>
            {wordsCredited ? t('plans.discountCode.wordsHint') : hasCode ? t('plans.discountCode.selectPlanHint') : t('plans.discountCode.description')}
          </Typography>
        </Box>

        <DialogContent sx={{ px: 3, pt: 3, pb: 3 }}>
          {wordsCredited && (
            <Box data-testid="discount-code-words" sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', textAlign: 'center' }}>
              {wordsCredited.code && <Chip label={wordsCredited.code} variant="outlined" sx={styles.codeChip} />}
              <Box sx={styles.benefit}>
                <Box sx={styles.benefitIcon}><AutoAwesomeOutlinedIcon /></Box>
                <Typography variant="h6" component="p" sx={{ fontWeight: 700 }}>
                  {t('plans.discountCode.wordsCredited', { words: formatLargeNumber(wordsCredited.words) })}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">{t('plans.discountCode.wordsUsage')}</Typography>
              <Button fullWidth size="large" variant="contained" color="primary" onClick={handleClose} sx={styles.primaryButton} data-testid="discount-code-words-done">
                {t('plans.discountCode.done')}
              </Button>
            </Box>
          )}

          {!hasCode && !wordsCredited && (
            <Box component="form" onSubmit={handleRedeem} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                autoFocus
                fullWidth
                placeholder={t('plans.discountCode.placeholder')}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  if (localError) setLocalError(null);
                }}
                disabled={discountPending}
                error={!!errorMessage}
                slotProps={{ htmlInput: { 'data-testid': 'discount-code-input', 'aria-label': t('plans.discountCode.label'), autoComplete: 'off', maxLength: 64 } }}
                sx={styles.codeInput}
              />
              <Button
                type="submit"
                fullWidth
                size="large"
                variant="contained"
                color="primary"
                disabled={discountPending || !code.trim()}
                data-testid="discount-code-apply"
                startIcon={discountPending ? <CircularProgress size={18} color="inherit" /> : null}
                sx={styles.primaryButton}
              >
                {t('plans.discountCode.apply')}
              </Button>
            </Box>
          )}

          {hasCode && !wordsCredited && (
            <Box data-testid="discount-code-active" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {t('plans.discountCode.active')}
                </Typography>
                <Chip label={discount.code} variant="outlined" sx={styles.codeChip} />
              </Box>

              {benefits.length > 0 && (
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {benefits.map(({ kind, text }) => {
                    const Icon = BENEFIT_ICONS[kind];
                    return (
                      <Box component="li" key={text} sx={styles.benefit}>
                        <Box sx={styles.benefitIcon}><Icon /></Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{text}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {expiryDate && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, color: 'text.secondary' }} data-testid="discount-code-expiry">
                  <ScheduleOutlinedIcon fontSize="small" />
                  <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                    <Typography variant="body2" component="span" sx={{ whiteSpace: 'nowrap' }}>
                      {t('plans.discountCode.expiresAt', { date: expiryDate })}
                    </Typography>
                    {expiresIn && (
                      <Typography variant="caption" component="span" sx={{ whiteSpace: 'nowrap' }}>
                        {t('plans.discountCode.expiresIn', expiresIn)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              <Button fullWidth size="large" variant="contained" color="primary" onClick={handleChoosePlan} sx={styles.primaryButton} data-testid="discount-code-choose-plan">
                {t('plans.discountCode.choosePlan')}
              </Button>
              <Button fullWidth variant="text" color="inherit" onClick={handleRemove} disabled={discountPending} data-testid="discount-code-remove" sx={{ color: 'text.secondary' }}>
                {t('plans.discountCode.remove')}
              </Button>
            </Box>
          )}

          {errorMessage && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }} data-testid="discount-code-error">
              {errorMessage}
            </Alert>
          )}
          {removed && !hasCode && !errorMessage && (
            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }} onClose={() => setRemoved(false)}>
              {t('plans.discountCode.removed')}
            </Alert>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DiscountCode;
